import { useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  costOf,
  formatMoney,
  formatQuantity,
  hoy,
  newId,
  parseMoney,
  parseQuantity,
  repartirProporcional,
  unitCostFromTotal,
  type Centavos,
} from '@abastece/shared';
import type { Producto } from '@abastece/core';
import { existenciaId } from '@abastece/core';
import { db } from '../db/dexie.js';
import { enRango } from '../lib/costos.js';
import { emitir } from '../lib/emitir.js';
import { anunciar } from '../lib/hecho.js';
import { usePuede } from '../lib/sesion.js';
import { volver } from '../lib/volver.js';
import { BuscadorProducto } from '../ui/BuscadorProducto.js';
import { Campo } from '../ui/Campo.js';
import { Pagina } from '../ui/Pagina.js';
import { Ico } from '../ui/Iconos.js';
import { useT } from '../i18n/index.js';
import { useNombreProducto } from '../i18n/productos.js';
import { etiquetaUnidad } from '../i18n/dominio.js';

interface Destino {
  key: string;
  producto: Producto;
  cantidad: string;
  /** How much of the source went into it, in the source's unit, when it is counted in another. */
  lleva: string;
}

/**
 * Reempaque: open bulk, produce packaged goods.
 *
 * Source and destination are different products — "Arroz a granel" becoming "Arroz 1 lb", each
 * with its own price and barcode. Adding a bag size to one product is a sale unit, not this.
 */
export function Reempaque() {
  const t = useT();
  const nombre = useNombreProducto();
  const existencias = useLiveQuery(() => db.existencias.toArray(), [], []);
  /**
   * What the bulk costs on average, and what that makes each packed product cost, are the books':
   * the owner's to see. A cashier repacks too since 2026-10-09, and saves the same without them.
   */
  const verCostos = usePuede('ver_costos');

  // Repacked in the shop, the one place there is (the owner, 2026-10-05).
  const ubicacion = 'tienda';
  const [origen, setOrigen] = useState<Producto | null>(null);
  const [consumido, setConsumido] = useState('');
  const [merma, setMerma] = useState('');
  const [costoAdicional, setCostoAdicional] = useState('');
  const [destinos, setDestinos] = useState<Destino[]>([]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stockOrigen = origen
    ? existencias.find((e) => e.id === existenciaId(origen.id, ubicacion))
    : undefined;

  const qConsumido = parseQuantity(consumido);
  const qMerma = parseQuantity(merma) ?? (0 as never);
  const extra = parseMoney(costoAdicional) ?? (0 as Centavos);

  /**
   * Two sizes of bag out of one sack share its cost by what each holds, not by how many bags
   * (`consumido` in the books): fifty bags of a pound and a hundred of half a pound are the same
   * rice each. Asked of every destination counted in another unit than the source, when there is
   * more than one; one in the source's own unit holds what it says.
   */
  const preguntaLleva = origen !== null && destinos.length > 1 && destinos.some((d) => d.producto.unidadBase !== origen.unidadBase);
  const pesos = destinos.map((d) =>
    preguntaLleva && d.producto.unidadBase !== origen!.unidadBase ? parseQuantity(d.lleva) : parseQuantity(d.cantidad),
  );
  const bueno = qConsumido !== null ? qConsumido - qMerma : null;
  const sumaLleva = pesos.reduce<number>((a, x) => a + (x ?? 0), 0);
  const llevaCuadra = !preguntaLleva || (pesos.every((x) => x !== null && x > 0) && sumaLleva === bueno);

  /**
   * Live preview of what each output will end up costing — the number that matters here. None
   * for figures past what the books take, as while a code scanned into one of these fields is
   * still there (`enRango`), and then nothing to save either.
   */
  const previsualizacion = useMemo(() => {
    if (!stockOrigen || qConsumido === null || qConsumido <= 0) return null;
    return enRango(() => {
      const valorConsumido = costOf(stockOrigen.costoPromedio, qConsumido);
      const valorMerma = costOf(stockOrigen.costoPromedio, qMerma);
      const valorBueno = (valorConsumido - valorMerma + extra) as Centavos;

      const cantidades = destinos.map((d) => parseQuantity(d.cantidad) ?? 0);
      if (cantidades.some((c) => c <= 0) || !llevaCuadra) return { valorConsumido, valorMerma, valorBueno, partes: null };

      // Shared as the books share it: by what each holds.
      const partes = repartirProporcional(valorBueno, pesos.map((x) => x ?? 0));
      return {
        valorConsumido,
        valorMerma,
        valorBueno,
        partes: destinos.map((d, i) => ({
          nombre: nombre(d.producto),
          unitario: unitCostFromTotal(partes[i]!, cantidades[i]! as never),
        })),
      };
    });
  }, [stockOrigen, qConsumido, qMerma, extra, destinos, nombre, llevaCuadra, pesos]);

  const valido =
    origen !== null &&
    qConsumido !== null &&
    qConsumido > 0 &&
    destinos.length > 0 &&
    destinos.every((d) => (parseQuantity(d.cantidad) ?? 0) > 0) &&
    llevaCuadra &&
    qMerma <= qConsumido &&
    (!stockOrigen || previsualizacion !== null);

  async function guardar() {
    if (!valido || !origen) return;
    setGuardando(true);
    setError(null);
    try {
      await emitir('inventario.reempaque', {
        reempaqueId: newId(),
        ubicacion,
        fecha: hoy(),
        origenProductoId: origen.id,
        origenCantidad: qConsumido!,
        mermaCantidad: qMerma,
        destinos: destinos.map((d, i) => ({
          productoId: d.producto.id,
          cantidad: parseQuantity(d.cantidad)!,
          ...(preguntaLleva ? { consumido: pesos[i]! } : {}),
        })),
        costoAdicional: extra,
      });
      // Back where it was begun: «Inventario», or «Por comprar» with the box to open.
      anunciar(t('reempaque.registrado'));
      void volver({ to: '/inventario' });
    } catch (e) {
      setError(e instanceof Error ? e.message : t('reempaque.noSePudo'));
      setGuardando(false);
    }
  }

  return (
    <Pagina titulo={t('reempaque.titulo')} volverA="/inventario">
      <section className="tarjeta mb-4 flex flex-col gap-4">
        {origen ? (
          <div className="flex items-center justify-between rounded-xl bg-slate-950/40 ring-1 ring-white/5 ring-inset px-4 py-3">
            <div>
              <p className="font-medium">{nombre(origen)}</p>
              <p className="tabular text-xs text-slate-500">
                {!stockOrigen
                  ? t('reempaque.sinExistencia')
                  : verCostos
                    ? t('reempaque.disponible', {
                        cantidad: formatQuantity(stockOrigen.cantidad),
                        unidad: etiquetaUnidad(origen.unidadBase, origen.unidadBase),
                        costo: formatMoney(costOf(stockOrigen.costoPromedio, 1_000 as never)),
                      })
                    : t('reempaque.hay', {
                        cantidad: formatQuantity(stockOrigen.cantidad),
                        unidad: etiquetaUnidad(origen.unidadBase, origen.unidadBase),
                      })}
              </p>
            </div>
            <button
              type="button"
              className="btn-texto gap-1 text-xs text-slate-500"
              onClick={() => setOrigen(null)}
            >
              <Ico icono="editar" className="size-3.5" />
              {t('reempaque.cambiar')}
            </button>
          </div>
        ) : (
          // One box on the screen at a time, so only one of them reads the scanner.
          <BuscadorProducto onElegir={setOrigen} etiqueta={t('reempaque.queSeAbre')} autoFocus lectorUsb preguntarEnHoja />
        )}

        {origen && (
          <div className="grid grid-cols-2 gap-4">
            <Campo
              etiqueta={t('reempaque.seConsume', {
                unidad: etiquetaUnidad(origen.unidadBase, origen.unidadBase),
              })}
              inputMode="decimal"
              className="tabular"
              value={consumido}
              onChange={(e) => setConsumido(e.target.value)}
            />
            <Campo
              etiqueta={t('reempaque.sePierde')}
              inputMode="decimal"
              className="tabular"
              value={merma}
              onChange={(e) => setMerma(e.target.value)}
              ayuda={t('reempaque.loDerramado')}
            />
          </div>
        )}
      </section>

      {origen && (
        <section className="tarjeta mb-4 flex flex-col gap-4">
          <h2 className="text-sm font-semibold text-slate-400">{t('reempaque.queSale')}</h2>
          <BuscadorProducto
            onElegir={(p) =>
              setDestinos((d) =>
                p.id === origen.id || d.some((x) => x.producto.id === p.id)
                  ? d
                  : [...d, { key: newId(), producto: p, cantidad: '', lleva: '' }],
              )
            }
            etiqueta=""
            lectorUsb
            preguntarEnHoja
          />

          {destinos.map((d) => (
            <div key={d.key} className="flex items-end gap-3">
              <div className="min-w-0 flex-1">
                <Campo
                  etiqueta={nombre(d.producto)}
                  inputMode="decimal"
                  className="tabular"
                  value={d.cantidad}
                  onChange={(e) =>
                    setDestinos((xs) =>
                      xs.map((x) => (x.key === d.key ? { ...x, cantidad: e.target.value } : x)),
                    )
                  }
                  ayuda={t('reempaque.enUnidad', {
                    unidad: etiquetaUnidad(d.producto.unidadBase, d.producto.unidadBase),
                  })}
                />
                {preguntaLleva && d.producto.unidadBase !== origen.unidadBase && (
                  <Campo
                    etiqueta={t('reempaque.lleva', {
                      origen: nombre(origen),
                      unidad: etiquetaUnidad(origen.unidadBase, origen.unidadBase),
                    })}
                    inputMode="decimal"
                    className="tabular mt-2"
                    value={d.lleva}
                    onChange={(e) =>
                      setDestinos((xs) => xs.map((x) => (x.key === d.key ? { ...x, lleva: e.target.value } : x)))
                    }
                  />
                )}
              </div>
              <button
                type="button"
                className="btn-texto mb-6 gap-1 text-xs text-slate-500"
                onClick={() => setDestinos((xs) => xs.filter((x) => x.key !== d.key))}
              >
                <Ico icono="quitar" className="size-3.5" />
                {t('comun.quitar')}
              </button>
            </div>
          ))}

          {preguntaLleva && !llevaCuadra && bueno !== null && pesos.every((x) => x !== null && x > 0) && (
            <p className="text-sm text-amber-300">
              {t('reempaque.llevaNoSuma', {
                suma: formatQuantity(sumaLleva as never),
                bueno: formatQuantity(bueno as never),
                unidad: etiquetaUnidad(origen.unidadBase, origen.unidadBase),
              })}
            </p>
          )}
          <Campo
            etiqueta={t('reempaque.costoEmpaque')}
            inputMode="decimal"
            className="tabular"
            value={costoAdicional}
            onChange={(e) => setCostoAdicional(e.target.value)}
            ayuda={t('reempaque.ayudaEmpaque')}
          />
        </section>
      )}

      {verCostos && previsualizacion && (
        <section className="tarjeta mb-4 flex flex-col gap-2">
          <h2 className="text-sm font-semibold text-slate-400">{t('reempaque.comoQueda')}</h2>
          <Fila
            etiqueta={t('reempaque.valorConsumido')}
            valor={formatMoney(previsualizacion.valorConsumido)}
          />
          {previsualizacion.valorMerma > 0 && (
            <Fila
              etiqueta={t('reempaque.valorMerma')}
              valor={`-${formatMoney(previsualizacion.valorMerma)}`}
            />
          )}
          {extra > 0 && <Fila etiqueta={t('reempaque.empaque')} valor={`+${formatMoney(extra)}`} />}
          <div className="border-t border-slate-800 pt-2">
            <Fila
              etiqueta={t('reempaque.vaAlEmpacado')}
              valor={formatMoney(previsualizacion.valorBueno)}
              fuerte
            />
          </div>
          {previsualizacion.partes?.map((p) => (
            <Fila
              key={p.nombre}
              etiqueta={t('reempaque.costoDe', { producto: p.nombre })}
              valor={formatMoney((p.unitario / 1_000) as Centavos)}
            />
          ))}
        </section>
      )}

      {error && <p className="mb-4 rounded-xl bg-rose-500/10 px-4 py-3 text-sm text-rose-300">{error}</p>}

      <button
        type="button"
        className="btn-primario w-full"
        disabled={!valido || guardando}
        onClick={() => void guardar()}
      >
        <Ico icono="guardar" />
        {t(guardando ? 'compra.registrando' : 'reempaque.registrar')}
      </button>
    </Pagina>
  );
}

function Fila({ etiqueta, valor, fuerte }: { etiqueta: string; valor: string; fuerte?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className={`text-sm ${fuerte ? 'font-medium' : 'text-slate-400'}`}>{etiqueta}</span>
      <span className={`tabular ${fuerte ? 'font-semibold' : 'text-sm'}`}>{valor}</span>
    </div>
  );
}
